/* global Office */
import React, { ReactElement, useCallback, useMemo, useState } from "react";
import { CoObjectItem, isObjectUpdatable } from "../interfaces/CoObject";
import HeaderPanel from "../layout/HeaderPanel";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { CommandBarButton, IconButton, ImageFit, Link, Stack } from "@fluentui/react";
import FooterPanel from "../layout/FooterPanel";
import { Checkbox } from "@progress/kendo-react-inputs";
import { selectContentControl } from "../../office/ContentControl";
import { useAppSelector } from "../../redux/hooks/hooks";
import { selectCpmUser, selectProtectionType } from "../../redux/feature/settings/configurations-slice";
import { OperationCompletedPanel } from "../layout/OperationCompletedPanel";
import { TrackedChangesModal } from "../coobjects/TrackedChangesModal";
import { isNotAuthorizedUser } from "../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../api/models/user-function-enum";
import { NotCompatibleWithWeb } from "../layout/ConfirmationModal";
import { ElaborationWrapper } from "../coobjects/ElaborationWrapper";
import { OBJECTS_LIST_NOTIFICATION } from "../../utils/constants/notification";
import { elaborationStatusItemIsBlocking } from "../../redux/feature/object/remote-elaborations-slice";
import { useFilteredElaborations } from "../generic/GenericElaborationSelector";

export interface ObjectsListProps {
  documentCoObjects: CoObjectItem[];
  documentId: string;
  updateSelectionOnly: boolean;
  showObjectIcons: boolean;
  mainHeaderTitle: string;
  footerComponents: (props: {
    selectedObjects: Map<string, CoObjectItem>;
    username: string;
    isNotAuthorized: boolean;
  }) => ReactElement;
  page: string;
}

export type TrackedObjChangeStatus = "ADD" | "EDIT" | "DELETE" | "UPDATE";
export type UserAction = "CREATE" | "EDIT" | "DELETE" | "RESTORE";

export interface Difference {
  key: string;
  previousValue: string | boolean | number | null;
  currentValue: string | boolean | number | null;
}
export interface TrackedObjChange {
  status: TrackedObjChangeStatus;
  date: string;
  username: string;
  differences: Difference[];
  userAction?: UserAction;
}

export interface ObjectsListState {
  showAvailableUpdates: boolean;
  selectedObjects: Map<string, CoObjectItem>;
  clickedObject?: string;
  trackChangesObjectItem: CoObjectItem | undefined;
}

const iconStyles = {
  root: {
    fontWeight: "bold",
  },
};

const checkboxStyles = {
  marginRight: "5px",
  // paddingRight: "5px",
  // paddingTop: "2px"
};

export const ObjectsListUpdatesPanel: React.FunctionComponent<ObjectsListProps> = (props) => {
  // component local state
  const [state, setState] = useState<ObjectsListState>({
    showAvailableUpdates: false,
    selectedObjects: new Map(),
    trackChangesObjectItem: undefined,
  });
  // application global state selectors
  const user = useAppSelector(selectCpmUser);

  let objects = props.documentCoObjects;

  const username: string = user.userName;

  const addedElaborationsDetails = useAppSelector((store) => store.remoteElaborations.addedElaborationItemsList);
  const blockingElabFindFunction = useCallback(
    (item) => elaborationStatusItemIsBlocking(item, addedElaborationsDetails),
    [addedElaborationsDetails]
  );
  const blockingElaboration = useFilteredElaborations({
    findFunction: blockingElabFindFunction,
  });

  // rendering business logic discriminators
  const showBackButtonFromUpdates = !props.updateSelectionOnly && state.showAvailableUpdates;
  const showUpdatesButton = !props.updateSelectionOnly && !state.showAvailableUpdates;
  const showSelectionControls =
    props.updateSelectionOnly || state.showAvailableUpdates || blockingElaboration != undefined;
  // --

  let notAuthorizedUser = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OBJECTLIST_PANEL_AVAILABLE_UPDATES,
    user.userFunctions
  );
  const protectionType: number = useAppSelector(selectProtectionType);

  if (state.showAvailableUpdates && Office.context.requirements.isSetSupported("WordApiOnline", "1.1"))
    return <NotCompatibleWithWeb />;

  return (
    <ElaborationWrapper notificationKey={OBJECTS_LIST_NOTIFICATION} elaborationItem={blockingElaboration}>
      <>
        <HeaderPanel applyPadding>
          <>
            {showBackButtonFromUpdates && (
              <CommandBarButton
                iconProps={{ iconName: "ChevronLeft", styles: iconStyles }}
                onClick={() => {
                  setState({ ...state, showAvailableUpdates: false });
                }}
                style={{
                  color: notAuthorizedUser ? "rgb(200, 198, 196)" : "var(--color-blue)",
                  cursor: notAuthorizedUser ? "default" : "pointer",
                }}
                disabled={notAuthorizedUser}
              ></CommandBarButton>
            )}
            {!state.showAvailableUpdates ? props.mainHeaderTitle : formatMessage("cof.objectslist.availableupdate")}
          </>
        </HeaderPanel>

        <div style={{ padding: "10px" }}>
          {objects.length == 0 ? (
            <OperationCompletedPanel
              message={
                props.page === "updates"
                  ? formatMessage("cof.documentmanagement.updates.message.anyfinddocumentpart")
                  : formatMessage("cof.editobject.message.anyfindobject")
              }
              alt={
                props.page === "updates"
                  ? formatMessage("cof.documentmanagement.updates.message.anyfinddocumentpart")
                  : formatMessage("cof.editobject.message.anyfindobject")
              }
              icon="/assets/noObject.svg"
            />
          ) : (
            <>
              {showUpdatesButton && (
                <>
                  <div>
                    <CommandBarButton
                      id="available_updates_button"
                      iconProps={{ iconName: "Refresh", styles: iconStyles }}
                      onClick={() => {
                        setState({ ...state, showAvailableUpdates: true });
                      }}
                      styles={{
                        flexContainer: {
                          flexDirection: "row-reverse",
                        },
                        label: {
                          fontWeight: "bold",
                        },
                      }}
                      style={{
                        float: "right",
                        marginBottom: "5px",
                        color: protectionType === 3 ? "var(--color-grey)" : "var(--color-blue)",
                      }}
                      text={formatMessage("cof.objectslist.availableupdate")}
                      disabled={protectionType === 3}
                    />
                    <br />
                  </div>
                  <div
                    style={{
                      borderBottom: `0.01rem solid ${"var(--color-grey)"}`,
                      width: "100%",
                      height: "3px",
                    }}
                  />
                </>
              )}
              <TrackedChangesModal
                onClose={() => setState((s) => ({ ...s, trackChangesObjectItem: undefined }))}
                trackChangesObjectItem={state.trackChangesObjectItem}
              />
              <ObjectList
                objects={objects}
                showSelectionControls={showSelectionControls}
                updatableSelected={state.showAvailableUpdates}
                selectedObjects={state.selectedObjects}
                toggleSelection={(objectItem) => {
                  let updatedMap: typeof state.selectedObjects;
                  if (state.selectedObjects.has(objectItem.id)) {
                    state.selectedObjects.delete(objectItem.id);
                    updatedMap = new Map(state.selectedObjects);
                  } else {
                    updatedMap = new Map(state.selectedObjects.set(objectItem.id, objectItem));
                  }
                  setState({ ...state, selectedObjects: updatedMap });
                }}
                onLinkClick={(objectItem) => {
                  selectContentControl(objectItem);
                  setState({ ...state, clickedObject: objectItem.id });
                }}
                onChangeCheckBox={() => {
                  if (state.selectedObjects.size == 0) {
                    const all = new Map(
                      objects.filter((objectItem) => isObjectUpdatable(objectItem)).map((obj) => [obj.id, obj])
                    );
                    setState({ ...state, selectedObjects: all });
                  } else {
                    setState({ ...state, selectedObjects: new Map() });
                  }
                }}
                trackChangesObjectItem={(item) => {
                  setState((s) => ({ ...s, trackChangesObjectItem: item }));
                }}
                showObjectIcons={props.showObjectIcons}
                clickedObject={state.clickedObject}
              />

              {showSelectionControls && (
                <FooterPanel>
                  <props.footerComponents
                    {...{
                      selectedObjects: state.selectedObjects,
                      username,
                      isNotAuthorized: isNotAuthorizedUser(
                        UserFunctionEnum.MENU_UPDATE_PANEL_GET_CHANGES,
                        user.userFunctions
                      ),
                    }}
                  />
                </FooterPanel>
              )}
            </>
          )}
        </div>
      </>
    </ElaborationWrapper>
  );
};

// object list subcomponent with component state access
const ObjectList = ({
  objects,
  showSelectionControls,
  updatableSelected,
  selectedObjects,
  toggleSelection,
  onLinkClick,
  onChangeCheckBox,
  showObjectIcons,
  clickedObject,
  trackChangesObjectItem,
}: {
  objects: CoObjectItem[];
  showSelectionControls: boolean;
  updatableSelected: boolean;
  selectedObjects: Map<string, CoObjectItem>;
  toggleSelection: (objectItem: CoObjectItem) => void;
  onLinkClick: (objectItem: CoObjectItem) => void;
  onChangeCheckBox: () => void;
  showObjectIcons: boolean;
  clickedObject: string | undefined;
  trackChangesObjectItem: (item: CoObjectItem) => void;
}) => {
  const numberOfUpdatableObjects = useMemo(
    () => objects.filter((objectItem) => isObjectUpdatable(objectItem)).length,
    [objects]
  );

  return (
    <div>
      {showSelectionControls && (
        <>
          <div style={{ marginBottom: "5px" }}>
            <Stack horizontal>
              <Checkbox
                id={"selectall"}
                value={selectedObjects.size != 0 && selectedObjects.size < numberOfUpdatableObjects ? null : true}
                checked={selectedObjects.size == numberOfUpdatableObjects}
                size="large"
                rounded="small"
                style={checkboxStyles}
                onChange={() => onChangeCheckBox()}
              />
              <div style={{ fontWeight: "bold", color: "var(--color-blue)" }}>
                {formatMessage("cof.objectlist.selectall")}
              </div>
            </Stack>
          </div>
        </>
      )}
      {objects.map((objectItem) => (
        <ObjectItemRow
          key={objectItem.id}
          objectItem={objectItem}
          updatableSelected={updatableSelected}
          showSelectionControls={showSelectionControls}
          selectedObjects={selectedObjects}
          toggleSelection={toggleSelection}
          trackChangesObjectItem={trackChangesObjectItem}
          showObjectIcons={showObjectIcons}
          clickedObject={clickedObject}
          onLinkClick={onLinkClick}
        />
      ))}
    </div>
  );
};

const ObjectItemRow = ({
  objectItem,
  updatableSelected,
  showSelectionControls,
  selectedObjects,
  toggleSelection,
  showObjectIcons,
  clickedObject,
  onLinkClick,
  trackChangesObjectItem,
}: {
  objectItem: CoObjectItem;
  showSelectionControls: boolean;
  updatableSelected: boolean;
  selectedObjects: Map<string, CoObjectItem>;
  toggleSelection: (objectItem: CoObjectItem) => void;
  showObjectIcons: boolean;
  clickedObject: string | undefined;
  onLinkClick: (objectitem: CoObjectItem) => void;
  trackChangesObjectItem: (item: CoObjectItem) => void;
}) => (
  <div key={`div-${objectItem.id}`}>
    {(!updatableSelected || (isObjectUpdatable(objectItem) && !objectItem.isLocked)) && (
      <Stack horizontal key={objectItem.id}>
        {showSelectionControls && (
          <Checkbox
            checked={selectedObjects.has(objectItem.id)}
            size="large"
            rounded="small"
            style={checkboxStyles}
            onChange={() => toggleSelection(objectItem)}
            key={`check-${objectItem.id}`}
          />
        )}
        <Link
          style={{ color: "var(--color-black)" }}
          key={`link-${objectItem.id}`}
          onClick={() => onLinkClick(objectItem)}
        >
          <div
            className={showObjectIcons ? `${objectItem.info.objectType} icon_class` : ""}
            style={clickedObject == objectItem.id ? { backgroundColor: "rgb(237, 235, 233)" } : {}}
          >
            {objectItem.info.objectName.toString().replace(/\.docx$/, "")}
            {objectItem.isLocked && (
              <img
                src="/assets/lock.svg"
                alt="Lock Icon"
                style={{
                  objectFit: "contain",
                  paddingLeft: "5px",
                  backgroundRepeat: "no",
                  backgroundPosition: "right center",
                }}
              />
            )}
            <span
              className={`${
                objectItem.info.isComponent
                  ? `component_title`
                  : objectItem.info.componentReference !== undefined
                  ? `instance_component_title`
                  : ``
              } right_icon_class`}
            />
          </div>
        </Link>
        {!showSelectionControls && (
          <IconButton
            iconProps={{
              imageProps: {
                src: "/assets/trackchanges.svg",
                imageFit: ImageFit.contain,
              },
              styles: iconStyles,
            }}
            style={{ marginLeft: "auto" }}
            onClick={() => trackChangesObjectItem(objectItem)}
          />
        )}
      </Stack>
    )}
  </div>
);
